import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { toCsv, parseCsv, download } from "@/lib/csv";
import { RuleNum } from "@/components/deck/RuleNum";
import { api, errMsg } from "@/lib/api";

const downloadBackup = async () => {
  try {
    const { data } = await api.get("/admin/backup/heroCodex");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([JSON.stringify(data, null, 1)], { type: "application/json" }));
    a.download = `heroCodex-backup-${data.pack}.json`;
    a.click();
  } catch (e) { toast.error(errMsg(e)); }
};

const COLS = ["name", "class", "scaler", "effect", "group", "baseIC", "bestIC", "notes"];

export const HeroesRules = ({ rules, update }) => {
  const file = useRef(null);
  const [q, setQ] = useState("");
  const [view, setView] = useState("grouped");
  const heroes = rules.heroes, groups = rules.groups;
  const setHero = (n, patch) => update({ heroes: { ...heroes, [n]: { ...heroes[n], ...patch } } });
  const names = useMemo(() => Object.keys(heroes).sort().filter((n) => n.toLowerCase().includes(q.toLowerCase())), [heroes, q]);
  const exportCsv = () => download("heroes-scaling.csv", toCsv(COLS, Object.keys(heroes).sort().map((n) => ({ name: n, ...heroes[n] }))));
  const importCsv = async (f) => {
    const rows = parseCsv(await f.text());
    if (!rows.length || !("name" in rows[0])) { toast.error("The CSV needs a 'name' column"); return; }
    const next = { ...heroes };
    rows.forEach((r) => {
      if (!r.name) return;
      next[r.name] = { ...(next[r.name] || {}), ...r, group: groups[r.group] ? r.group : (next[r.name]?.group || "control"),
        baseIC: r.baseIC === "" ? null : Number(r.baseIC), bestIC: Number(r.bestIC) || 0 };
      delete next[r.name].name;
    });
    update({ heroes: next });
    toast.success(`Loaded ${rows.length} heroes. Press Save rules to keep them.`);
  };
  const row = (n) => {
    const h = heroes[n];
    return (
      <tr key={n} data-testid={`hero-row-${n}`}>
        <td className="lg-nm">{n}</td><td>{h.class}</td><td>{h.scaler}{h.effect ? ` · ${h.effect}` : ""}</td>
        <td><select className="lg-select lg-mini" value={h.group} onChange={(e) => setHero(n, { group: e.target.value })} data-testid={`hero-group-${n}`}>
          {Object.entries(groups).map(([k, g]) => <option key={k} value={k}>{g.label}</option>)}
        </select></td>
        <td>{h.baseIC ?? "—"}</td>
        <td><RuleNum value={h.bestIC ?? 0} onChange={(v) => setHero(n, { bestIC: v })} testid={`hero-best-${n}`} w={56} /></td>
        <td><input className="lg-input lg-mini" style={{ minWidth: 160 }} value={h.notes || ""} onChange={(e) => setHero(n, { notes: e.target.value })} data-testid={`hero-notes-${n}`} /></td>
      </tr>
    );
  };
  const head = <thead><tr><th>Hero</th><th>Class</th><th>Ability scales on</th><th>Grading group</th><th>Base IC (A5)</th><th>Best IC</th><th>Notes</th></tr></thead>;
  return (
    <div data-testid="rules-heroes">
      <p style={{ fontSize: 13, marginBottom: 8 }}>I read every ability in the Hero Codex and sorted the heroes by the stat their ability grows from. Check each group and move any hero that's in the wrong one. "Best IC" is the lowest Initial Charge that hero can reach (0 for most).</p>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <input className="lg-input lg-mini" style={{ width: 200 }} placeholder="Search heroes" value={q} onChange={(e) => setQ(e.target.value)} data-testid="heroes-search" />
        <button className={`lg-chip ${view === "grouped" ? "on" : ""}`} onClick={() => setView("grouped")} data-testid="heroes-view-grouped">By group</button>
        <button className={`lg-chip ${view === "all" ? "on" : ""}`} onClick={() => setView("all")} data-testid="heroes-view-all">A–Z</button>
        <button className="lg-btn ghost sm" onClick={exportCsv} data-testid="heroes-export">Export CSV</button>
        <button className="lg-btn ghost sm" onClick={() => file.current.click()} data-testid="heroes-import">Import CSV</button>
        <input ref={file} type="file" accept=".csv,text/csv" hidden onChange={(e) => { if (e.target.files[0]) importCsv(e.target.files[0]); e.target.value = ""; }} />
        <button className="lg-btn ghost sm" onClick={downloadBackup} data-testid="heroes-codex-backup">Download Hero Codex backup</button>
      </div>
      {view === "all" ? (
        <div className="lg-scroll"><table className="lg-itable">{head}<tbody>{names.map(row)}</tbody></table></div>
      ) : Object.entries(groups).map(([k, g]) => {
        const list = names.filter((n) => heroes[n].group === k);
        return (
          <details key={k} open={!!q} className="lg-sub" data-testid={`heroes-group-${k}`}>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>{g.label} · {list.length} heroes</summary>
            <div className="lg-scroll" style={{ marginTop: 8 }}><table className="lg-itable">{head}<tbody>{list.map(row)}</tbody></table></div>
          </details>
        );
      })}
    </div>
  );
};
